import React, { useEffect, useMemo, useState } from "react";
import { Select, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";

import type { BusinessTrip } from "types/BusinessTrip";
import { fetchUserBusinessTripsForLeave } from "services/business-trip.service";

export interface BusinessTripFilterContext {
  companyId?: string;
  startDate?: string;
  endDate?: string;
}

interface BusinessTripCustomFieldSelectProps {
  label: string;
  value: string;
  required?: boolean;
  error?: string;
  context?: BusinessTripFilterContext;
  onChange: (value: string) => void;
}

function tripLabel(trip: BusinessTrip): string {
  const place = String(trip.placeOfBusinessTrip || "").trim();
  const dateFrom = trip.dateFrom ? new Date(trip.dateFrom)?.toISOString()?.split('T')[0] : '';
  const dateTo = trip.dateTo ? new Date(trip.dateTo)?.toISOString()?.split('T')[0] : '';
  const base = `${dateFrom} -> ${dateTo}`;
  return place ? `${base} (${place})` : base;
}

export default function BusinessTripCustomFieldSelect({
  label,
  value,
  required = false,
  error,
  context,
  onChange,
}: BusinessTripCustomFieldSelectProps) {
  const { t } = useTranslation();
  const [trips, setTrips] = useState<BusinessTrip[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const canFetch = Boolean(
    context?.companyId && context?.startDate && context?.endDate
  );
  const fetchKey = canFetch
    ? [context?.companyId, context?.startDate, context?.endDate].join("|")
    : "";
  const [loadedFetchKey, setLoadedFetchKey] = useState<string | null>(null);

  useEffect(() => {
    if (!canFetch || !context?.companyId) {
      setTrips([]);
      setLoadedFetchKey(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const data = await fetchUserBusinessTripsForLeave(context.companyId!, {
          startDate: context.startDate,
          endDate: context.endDate,
        });
        if (cancelled) return;
        setTrips(Array.isArray(data) ? data : []);
      } catch (_e) {
        if (!cancelled) setTrips([]);
      } finally {
        if (!cancelled) {
          setLoadedFetchKey(fetchKey);
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    canFetch,
    context?.companyId,
    context?.startDate,
    context?.endDate,
    fetchKey,
  ]);

  useEffect(() => {
    if (!value) return;
    if (!canFetch || isLoading || loadedFetchKey !== fetchKey) return;
    const exists = trips.some((trip) => String(trip.id) === value);
    if (!exists) onChange("");
  }, [trips, value, onChange, canFetch, isLoading, loadedFetchKey, fetchKey]);

  const options = useMemo(
    () => [
      {
        label: t("files_template_custom_type_select_select_an_option"),
        value: "",
      },
      ...trips
        .filter((trip) => Boolean(trip.id))
        .map((trip) => ({
          label: tripLabel(trip),
          value: String(trip.id),
        })),
    ],
    [trips, t]
  );

  return (
    <div>
      <Select
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        requiredIndicator={required}
        error={error}
        disabled={!canFetch || isLoading}
      />
      {!canFetch && (
        <Text as="p" variant="bodySm" tone="subdued">
          {t("leave_calendar_trip_select_dates_hint", {
            defaultValue: "Select start and end dates to load available trips.",
          })}
        </Text>
      )}
      {canFetch && !isLoading && options.length <= 1 && (
        <Text as="p" variant="bodySm" tone="critical">
          {t("leave_calendar_trip_not_found_for_dates", {
            defaultValue:
              "No business trips found for this user in the selected date range.",
          })}
        </Text>
      )}
    </div>
  );
}
